// FunctionStrip — replaces freeform suggestion chips with a tight row of
// "what do you want to do?" actions. Each one focuses the composer and
// switches it to the best model for that capability. Image / Build / Agent
// pop out the dedicated surface since MultiChat is chat-only today.
import { MessageSquare, Image as ImageIcon, Globe, BarChart3, Code2, Bot } from "lucide-react";
import { defaultModelFor, type Capability } from "@/lib/aiModels";
import { cn } from "@/lib/utils";

type Fn = {
  key: string;
  label: string;
  Icon: any;
  hint: string;
} & (
  | { kind: "model"; capability: Capability; placeholder: string }
  | { kind: "popout"; to: string }
);

const FUNCTIONS: Fn[] = [
  { key: "chat",     kind: "model",  capability: "chat",     label: "Chat",     Icon: MessageSquare, hint: "Ask anything",        placeholder: "Ask anything…" },
  { key: "research", kind: "model",  capability: "research", label: "Research", Icon: Globe,         hint: "Live web + citations", placeholder: "Research a topic with live sources…" },
  { key: "analyze",  kind: "model",  capability: "analyze",  label: "Analyze",  Icon: BarChart3,     hint: "Reason over data",     placeholder: "Paste data, docs, or numbers to analyze…" },
  { key: "code",     kind: "model",  capability: "chat",     label: "Code",     Icon: Code2,         hint: "Write & debug code",   placeholder: "Describe code to write, fix, or explain…" },
  { key: "image",    kind: "popout", to: "/workspace",       label: "Image",    Icon: ImageIcon,     hint: "Generate images" },
  { key: "agent",    kind: "popout", to: "/agents",          label: "Agent",    Icon: Bot,           hint: "Multi-step agents" },
];

// Code routes to a chat model but the strongest one available cheaply.
const CODE_MODEL_ID = "openai/gpt-5-mini";

export default function FunctionStrip() {
  const pick = (fn: Fn) => {
    if (fn.kind === "popout") {
      window.dispatchEvent(new CustomEvent("open-popout", { detail: { to: fn.to, label: fn.label } }));
      return;
    }
    const modelId = fn.key === "code" ? CODE_MODEL_ID : defaultModelFor(fn.capability).id;
    window.dispatchEvent(new CustomEvent("multichat-set-mode", {
      detail: { modelId, placeholder: fn.placeholder },
    }));
  };

  return (
    <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2">
      {FUNCTIONS.map((fn) => {
        const Icon = fn.Icon;
        return (
          <button
            key={fn.key}
            type="button"
            onClick={() => pick(fn)}
            title={fn.hint}
            className={cn(
              "group flex flex-col items-center gap-1 py-3 px-2 rounded-xl",
              "border border-border bg-card/40 hover:border-primary/50 hover:bg-card",
              "text-muted-foreground hover:text-foreground transition-all",
            )}
          >
            <Icon className="w-4 h-4 text-primary/80 group-hover:text-primary" />
            <span className="text-[11px] font-mono">{fn.label}</span>
          </button>
        );
      })}
    </div>
  );
}